import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSync, faXmarkCircle, faPencil, faXmark } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { RadioSelect } from '@shared/components/ui/RadioSelect';
import { Input } from '@shared/components/ui/input';
import { Separator, Switch } from '@shared/components/ui/shadcn';
import { ShopsModel, CHANNELS_MINIMAL_SETTINGS } from '../../models/Shops';
import { ShopAvatar } from '../display/ShopAvatar';
import { usePaymentMethods } from '../../hooks';
import { useSettingsManager } from '../../hooks';
import { PaymentMethodsMobileModal, PaymentMethodsMobileModalRef } from './PaymentMethodsMobileModal';
import { ImportFiltersSummary } from '../interaction';

export interface ShopSettingsMobileModalRef {
  open: (shop: ShopsModel) => void;
}

export const ShopSettingsMobileModal = forwardRef<ShopSettingsMobileModalRef>((_, ref) => {
  const { t } = useTranslation('shopsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const paymentMethodsModalRef = useRef<PaymentMethodsMobileModalRef>(null);
  const [shop, setShop] = useState<ShopsModel | null>(null);
  const [approveDefault, setApproveDefault] = useState('');
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('');
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  // Cargar medios de pago
  const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();

  // Hook para manejar métodos de pago
  const { linkedPaymentMethods, updatePaymentMethods, getUpdatedShop, saveSettings, isSaving } = useSettingsManager();

  // 🎯 Funciones para manejar edición del nombre
  const handleStartEditName = () => {
    setEditedName(shop?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName('');
    setIsEditingName(false);
  };

  const handleSaveName = (onSuccess?: () => void) => {
    if (shop && editedName.trim() !== shop.name) {
      const shopToUse = { ...shop, name: editedName.trim() };
      saveSettings(
        shopToUse,
        {
          approveDefault,
          defaultPaymentMethod,
          trackingEnabled,
        },
        () => {
          setShop(shopToUse);
          setIsEditingName(false);
          onSuccess?.();
        },
      );
    } else {
      setIsEditingName(false);
    }
  };

  const handleMinimalSave = () => {
    const shopToUse = isEditingName ? { ...shop!, name: editedName.trim() } : shop!;
    saveSettings(
      shopToUse,
      {
        approveDefault,
        defaultPaymentMethod,
        trackingEnabled,
      },
      () => modalRef.current?.closeModal(),
    );
  };

  // Manejar apertura del modal de métodos de pago
  const handleOpenPaymentMethodsModal = () => {
    if (shop) {
      // Usar el shop actualizado con los métodos de pago modificados
      const currentShop = getUpdatedShop(shop);

      paymentMethodsModalRef.current?.open(currentShop, (updatedMethods) => {
        updatePaymentMethods(updatedMethods);
        // Actualizar el shop local para que los cambios persistan
        setShop({
          ...shop,
          linked_payment_methods: updatedMethods,
        });
      });
    }
  };

  useImperativeHandle(ref, () => ({
    open: (selectedShop) => {
      setShop(selectedShop);
      // Inicializar con el valor de approval_type del objeto shop
      setApproveDefault(selectedShop.approval_type.toString());
      // Inicializar con el valor de default_payment_method_id del objeto shop
      setDefaultPaymentMethod(selectedShop.default_payment_method_id?.toString() || '');
      // Inicializar el switch con el valor de fulfillment
      setTrackingEnabled(selectedShop.fulfillment !== null);
      // Inicializar los métodos de pago vinculados
      updatePaymentMethods(selectedShop.linked_payment_methods || []);

      // Pequeño retraso para asegurar que el modal esté montado
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.openModal();
        }
      }, 0);
    },
  }));

  if (!shop) return null;

  const isMinimalSettings = CHANNELS_MINIMAL_SETTINGS.includes(shop.channel_id);

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('modals.settings.general.title')}
        type="default"
        width="lg"
        className="bg-body-bg !outline-0"
        fullScreenMobile={true}
      >
        <div className="flex flex-col justify-between gap-4 h-full pt-4">
          <div className="flex flex-col gap-4">
            {/* Header con imagen y badges */}
            <div className="flex items-center gap-4">
              <ShopAvatar shop={shop} size="lg" />
              <div className="flex-1">
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName();
                        }
                      }}
                      className="text-lg font-medium"
                      data-testid="shop-name-input-mobile"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEditName}
                      className="!p-2"
                      data-testid="cancel-name-button-mobile"
                    >
                      <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">{shop.name}</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleStartEditName}
                      className="!p-2"
                      data-testid="edit-name-button-mobile"
                    >
                      <FontAwesomeIcon icon={faPencil} className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Badge
                    variant="outline"
                    className={`bg-card-border border-0 text-xs py-1 ${
                      shop.status > 0 ? 'light:bg-bg-success' : 'light:bg-bg-error'
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={shop.status > 0 ? faCheckCircle : faXmarkCircle}
                      className={`mr-1 ${shop.status > 0 ? 'text-success' : 'text-error'}`}
                    />
                    {shop.status > 0 ? t('modals.settings.general.active') : t('modals.settings.general.inactive')}
                  </Badge>
                  <Badge variant="outline" className="bg-card-border border-0 text-xs py-1 light:bg-bg-success">
                    <FontAwesomeIcon icon={faSync} className="text-success mr-1" />
                    {t('modals.settings.general.synced')}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Configuración General - oculta para canales mínimos (solo nombre) */}
            {!isMinimalSettings && (
              <section>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2 items-center">
                      <label className="text-sm">{t('modals.settings.general.approveDefault.label')}</label>
                      <InfoTooltip text={t('modals.settings.general.approveDefaultTooltip')} />
                    </div>
                    <RadioSelect
                      options={[
                        {
                          value: '0',
                          label: t('modals.settings.general.approveDefault.none.label'),
                          description: t('modals.settings.general.approveDefault.none.description'),
                        },
                        {
                          value: '1',
                          label: t('modals.settings.general.approveDefault.all.label'),
                          description: t('modals.settings.general.approveDefault.all.description'),
                        },
                        {
                          value: '2',
                          label: t('modals.settings.general.approveDefault.paid.label'),
                          description: t('modals.settings.general.approveDefault.paid.description'),
                        },
                      ]}
                      value={approveDefault}
                      setValue={setApproveDefault}
                      placeholder={t('modals.settings.general.selectPlaceholder')}
                      className="w-full"
                      dropdownWidth="md"
                      dataTestId="approve-default-select"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Configuración de Seguimiento - oculta para canales mínimos */}
            {!isMinimalSettings && (
              <section>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2 items-center">
                    <label className="text-sm font-medium">{t('modals.settings.general.uploadTracking')}</label>
                    <InfoTooltip text={t('modals.settings.general.uploadTrackingTooltip')} />
                  </div>
                  <Switch id="tracking-switch" checked={trackingEnabled} onCheckedChange={setTrackingEnabled} />
                </div>
              </section>
            )}

            {!isMinimalSettings && <Separator />}

            {/* Configuración de Pagos - oculta para canales mínimos */}
            {!isMinimalSettings && (
              <section>
                <h4 className="text-sm font-medium mb-3">{t('modals.settings.payments.title')}</h4>
                <div className="space-y-3">
                  <div className="flex gap-2 items-center justify-between">
                    <label className="text-sm mb-2 block">{t('modals.settings.payments.defaultPaymentMethod')}</label>
                    <RadioSelect
                      options={paymentMethods.map((method) => ({
                        value: method.id,
                        label: method.name,
                      }))}
                      value={defaultPaymentMethod}
                      setValue={setDefaultPaymentMethod}
                      placeholder={
                        paymentMethodsLoading ? t('common.loading') : t('modals.settings.general.selectPlaceholder')
                      }
                      dropdownWidth="sm"
                      dataTestId="default-payment-method-select"
                      disabled={paymentMethodsLoading}
                    />
                  </div>
                  <div className="flex gap-2 items-center justify-between">
                    <div>
                      {/* Círculos superpuestos dinámicos basados en linked_payment_methods */}
                      <div className="flex items-center">
                        {linkedPaymentMethods.map((method, index) => (
                          <div
                            key={method.id}
                            className={`w-9 h-9 bg-card-bg rounded-full flex items-center justify-center text-lg font-medium border border-card-border overflow-hidden text-title relative ${
                              index > 0 ? '-ml-4' : ''
                            }`}
                            style={{
                              zIndex: index + 1,
                            }}
                          >
                            <span className="text-xs font-bold text-white">{method.title.charAt(0).toUpperCase()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div
                      className="text-sm font-medium cursor-pointer hover:text-primary transition-colors"
                      onClick={handleOpenPaymentMethodsModal}
                      data-testid="open-payment-methods-button"
                    >
                      {t('modals.settings.payments.add')}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {!isMinimalSettings && <Separator />}

            {/* Filtros de Importación - ocultos para canales mínimos */}
            {!isMinimalSettings && (
              <section>
                <div className="w-full">
                  <ImportFiltersSummary
                    shopId={shop.id}
                    initialFilters={shop.import_filters}
                    onFiltersUpdated={(newFilters) => {
                      setShop((prev) => (prev ? { ...prev, import_filters: newFilters } : null));
                    }}
                  />
                </div>
              </section>
            )}
          </div>
          {/* Botones de acción */}
          <div className="flex gap-2 justify-end pb-4">
            <Button
              variant="outline"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="cancel-settings-button"
            >
              {t('modals.settings.buttons.cancel')}
            </Button>
            {isMinimalSettings ? (
              <Button variant="primary" onClick={handleMinimalSave} disabled={isSaving}>
                {isSaving ? t('common.loading') : t('modals.settings.buttons.save')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  if (shop) {
                    const shopToUse = isEditingName ? { ...shop, name: editedName.trim() } : shop;

                    saveSettings(
                      shopToUse,
                      {
                        approveDefault,
                        defaultPaymentMethod,
                        trackingEnabled,
                      },
                      () => modalRef.current?.closeModal(),
                    );
                  }
                }}
                disabled={isSaving}
              >
                {isSaving ? t('common.loading') : t('modals.settings.buttons.save')}
              </Button>
            )}
          </div>
        </div>
      </ModalCenter>

      {/* Modal de métodos de pago */}
      <PaymentMethodsMobileModal ref={paymentMethodsModalRef} />
    </>
  );
});

ShopSettingsMobileModal.displayName = 'ShopSettingsMobileModal';
